// src/pages/Home.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    ArrowRight,
    Calendar,
    Award,
    Film,
    Camera,
    Monitor,
    Cpu,
    BookOpen,
    Briefcase,
    Code,
    Layers,
    ExternalLink,
    Zap,
    Database,
    Shield
} from 'lucide-react';
import SEOLayout from '../components/layout/SEOLayout';
import Newsletter from '../components/Newsletter';
import SumerianHero from '../components/SumerianHero';

const Home = () => {
    const [bioData, setBioData] = useState(null);
    const [portfolioData, setPortfolioData] = useState(null);
    const [companiesData, setCompaniesData] = useState(null);
    const [pressData, setPressData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [bioRes, portfolioRes, companiesRes, pressRes] = await Promise.all([
                    fetch('/api/content/bio'),
                    fetch('/api/content/portfolio'),
                    fetch('/api/content/companies'),
                    fetch('/api/content/press')
                ]);

                const [bio, portfolio, companies, press] = await Promise.all([
                    bioRes.json(),
                    portfolioRes.json(),
                    companiesRes.json(),
                    pressRes.json()
                ]);

                setBioData(bio);
                setPortfolioData(portfolio);
                setCompaniesData(companies);
                setPressData(press);
            } catch (error) {
                console.error('Error loading content:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const iconMap = {
        Calendar, Award, Film, Camera, Monitor, Cpu, BookOpen, Briefcase, Code, Layers
    };

    if (loading) {
        return (
            <SEOLayout>
                <div className="min-h-screen flex items-center justify-center bg-gray-900">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-400"></div>
                </div>
            </SEOLayout>
        );
    }

    const featuredProjects = portfolioData?.projects?.filter(p => p.featured)?.slice(0, 3) || [];
    const subsidiaries = companiesData?.subsidiary_companies?.slice(0, 2) || [];
    const featuredPress = pressData?.featured_recognition?.slice(0, 2) || [];

    const handlePrimaryClick = () => {
        navigate(bioData?.hero?.cta?.primary?.link || "/portfolio");
    };

    const handleSecondaryClick = () => {
        navigate(bioData?.hero?.cta?.secondary?.link || "/about");
    };

    return (
        <SEOLayout
            title="Technology Solutions - Flashing Like Lightning"
            description={bioData?.hero?.description}
            keywords="Baraqu LLC, technology solutions, systematic innovation, VR development, specialized hardware"
        >
            {/* Sumerian Hero Section */}
            <SumerianHero
                heroData={bioData?.hero}
                onPrimaryClick={handlePrimaryClick}
                onSecondaryClick={handleSecondaryClick}
            />

            {/* Core Capabilities */}
            <section className="py-20 bg-white" aria-labelledby="capabilities-heading">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 id="capabilities-heading" className="text-3xl md:text-4xl font-light text-gray-900 mb-4">
                            Core Technologies
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Systematic innovation across six specialized domains
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {bioData?.capabilities?.categories?.map((capability, index) => {
                            const IconComponent = iconMap[capability.icon] || Code;
                            return (
                                <div key={index} className="group">
                                    <div className="bg-gray-50 p-8 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                                        <div className="flex items-center mb-4">
                                            <div className="p-2 bg-blue-100 rounded-lg">
                                                <IconComponent className="h-6 w-6 text-blue-600" />
                                            </div>
                                            <h3 className="text-xl font-medium text-gray-900 ml-3">
                                                {capability.name}
                                            </h3>
                                        </div>
                                        <p className="text-gray-600 leading-relaxed">
                                            {capability.description}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Featured Solutions */}
            <section className="py-20 bg-gray-50" aria-labelledby="solutions-heading">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 id="solutions-heading" className="text-3xl md:text-4xl font-light text-gray-900 mb-4">
                            Featured Solutions
                        </h2>
                        <p className="text-xl text-gray-600">
                            Technology that endures like stone, performs like lightning
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {featuredProjects.map((project) => (
                            <article key={project.id} className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                                <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                                    <img
                                        className="w-full h-48 object-cover"
                                        src={project.image}
                                        alt={`${project.title} solution`}
                                        loading="lazy"
                                    />
                                </div>
                                <div className="p-6">
                                    <div className="flex items-center space-x-2 mb-3">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                            {project.category.replace('_', ' ').toUpperCase()}
                                        </span>
                                        <span className="text-sm text-gray-500">{project.year}</span>
                                    </div>
                                    <h3 className="text-xl font-medium text-gray-900 mb-3">
                                        {project.title}
                                    </h3>
                                    <p className="text-gray-600 mb-4 line-clamp-3">
                                        {project.description}
                                    </p>
                                    <div className="flex justify-between items-center">
                                        <p className="text-sm text-gray-500">
                                            {project.client}
                                        </p>
                                        {project.url && (
                                            <a
                                                href={project.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                                            >
                                                Learn More
                                                <ExternalLink className="ml-1 h-4 w-4" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>

                    <div className="text-center mt-12">
                        <Link
                            to="/portfolio"
                            className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors duration-200"
                        >
                            View All Solutions
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Methodology */}
            <section className="py-20 bg-gray-900" aria-labelledby="methodology-heading">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 id="methodology-heading" className="text-3xl md:text-4xl font-light text-white mb-4">
                            The Baraqu Methodology
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Systematic innovation principles refined over 5,000 years
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <blockquote className="text-2xl text-gray-300 italic mb-8 leading-relaxed">
                                "{bioData?.philosophy?.quote}"
                            </blockquote>
                            <div className="space-y-6 text-gray-400">
                                <p>{bioData?.philosophy?.foundation}</p>
                                <p>{bioData?.philosophy?.methodology}</p>
                            </div>
                        </div>

                        <div className="space-y-6">
                            {portfolioData?.methodology?.principles?.map((principle, index) => (
                                <div key={index} className="flex items-start space-x-4">
                                    <div className="flex-shrink-0 p-2 bg-blue-900/50 rounded-lg">
                                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-medium text-white mb-2">
                                            {principle.name}
                                        </h3>
                                        <p className="text-gray-400">
                                            {principle.description}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/*/!* Subsidiary Companies *!/*/}
            {/*<section className="py-20 bg-white" aria-labelledby="subsidiaries-heading">*/}
            {/*    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">*/}
            {/*        <div className="text-center mb-16">*/}
            {/*            <h2 id="subsidiaries-heading" className="text-3xl md:text-4xl font-light text-gray-900 mb-4">*/}
            {/*                Specialized Solutions*/}
            {/*            </h2>*/}
            {/*            <p className="text-xl text-gray-600">*/}
            {/*                Focused subsidiaries serving distinct market needs*/}
            {/*            </p>*/}
            {/*        </div>*/}

            {/*        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">*/}
            {/*            {subsidiaries.map((company) => (*/}
            {/*                <div key={company.id} className="bg-gray-50 p-8 rounded-lg">*/}
            {/*                    <div className="flex items-center space-x-3 mb-4">*/}
            {/*                        <Shield className="h-6 w-6 text-blue-600" />*/}
            {/*                        <h3 className="text-2xl font-medium text-gray-900">*/}
            {/*                            {company.name}*/}
            {/*                        </h3>*/}
            {/*                    </div>*/}
            {/*                    <p className="text-blue-600 font-medium mb-4">{company.tagline}</p>*/}
            {/*                    <p className="text-gray-600 mb-6">{company.description}</p>*/}
            {/*                    <a*/}
            {/*                        href={company.website}*/}
            {/*                        target="_blank"*/}
            {/*                        rel="noopener noreferrer"*/}
            {/*                        className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"*/}
            {/*                    >*/}
            {/*                        Visit {company.name}*/}
            {/*                        <ExternalLink className="ml-1 h-4 w-4" />*/}
            {/*                    </a>*/}
            {/*                </div>*/}
            {/*            ))}*/}
            {/*        </div>*/}

            {/*        <div className="text-center mt-12">*/}
            {/*            <Link*/}
            {/*                to="/companies"*/}
            {/*                className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors duration-200"*/}
            {/*            >*/}
            {/*                Explore Ecosystem*/}
            {/*                <ArrowRight className="ml-2 h-5 w-5" />*/}
            {/*            </Link>*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*</section>*/}

            {/*/!* Recognition *!/*/}
            {/*<section className="py-20 bg-gray-50" aria-labelledby="recognition-heading">*/}
            {/*    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">*/}
            {/*        <div className="text-center mb-16">*/}
            {/*            <h2 id="recognition-heading" className="text-3xl md:text-4xl font-light text-gray-900 mb-4">*/}
            {/*                Industry Recognition*/}
            {/*            </h2>*/}
            {/*            <p className="text-xl text-gray-600">*/}
            {/*                Innovation acknowledged across multiple sectors*/}
            {/*            </p>*/}
            {/*        </div>*/}

            {/*        <div className="space-y-6">*/}
            {/*            {featuredPress.map((item) => (*/}
            {/*                <div key={item.id} className="bg-white p-6 rounded-lg border border-gray-200">*/}
            {/*                    <div className="flex justify-between items-start">*/}
            {/*                        <div className="flex-1">*/}
            {/*                            <div className="flex items-center space-x-3 mb-2">*/}
            {/*                                <Award className="h-5 w-5 text-blue-600" />*/}
            {/*                                <span className="text-sm font-medium text-blue-600">*/}
            {/*                                    {item.organization}*/}
            {/*                                </span>*/}
            {/*                                <span className="text-sm text-gray-500">*/}
            {/*                                    {new Date(item.date).toLocaleDateString()}*/}
            {/*                                </span>*/}
            {/*                            </div>*/}
            {/*                            <h3 className="text-xl font-medium text-gray-900 mb-2">*/}
            {/*                                {item.title}*/}
            {/*                            </h3>*/}
            {/*                            <p className="text-gray-600">{item.description}</p>*/}
            {/*                        </div>*/}
            {/*                    </div>*/}
            {/*                </div>*/}
            {/*            ))}*/}
            {/*        </div>*/}


            {/*    </div>*/}
            {/*</section>*/}

            {/* CTA Section */}
            <section className="py-20 bg-gray-900" aria-labelledby="cta-heading">
                <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
                    <Zap className="h-12 w-12 text-blue-400 mx-auto mb-6" />
                    <h2 id="cta-heading" className="text-3xl md:text-4xl font-light text-white mb-6">
                        Ready to Illuminate Your Path Forward?
                    </h2>
                    <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                        Transform your challenges into lasting solutions with systematic innovation that endures.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900"
                    >
                        Start a Conversation
                        <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                </div>
            </section>
        </SEOLayout>
    );
};

export default Home;